/**
 * School Data Checker
 *
 * Checks:
 * 1. Does schools collection exist?
 * 2. Does school ID "hktnikYSv6l6vGPbjMmG" exist?
 * 3. Can current user read it?
 * 4. What's the user's schoolId vs schoolIds?
 *
 * Usage: await window.checkSchoolsData()
 */

import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { auth } from '../firebase';

export async function checkSchoolsData() {
  try {
    const currentUser = auth.currentUser;

    if (!currentUser) {
      console.error('❌ No user logged in');
      return;
    }

    console.log('\n' + '='.repeat(80));
    console.log('SCHOOL DATA CHECK');
    console.log('='.repeat(80));
    console.log(`\n👤 Current User: ${currentUser.email} (${currentUser.uid})\n`);

    // 1. Check user document for schoolId fields
    console.log('📋 USER DOCUMENT CHECK');
    console.log('-'.repeat(80));
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const userData = userSnap.data();
        console.log('✅ User document exists');
        console.log(`   schoolId (singular): ${userData.schoolId || 'null'}`);
        console.log(`   schoolIds (plural array): ${JSON.stringify(userData.schoolIds || [])}`);
        console.log(`   assignedSchoolId: ${userData.assignedSchoolId || 'null'}`);
        console.log(`   parent: ${userData.parent}`);
        console.log(`   staff: ${userData.staff}`);
        console.log(`   admin: ${userData.admin}`);
      } else {
        console.log('❌ User document does not exist');
      }
    } catch (error) {
      console.error('❌ Error reading user document:', error.message);
    }

    // 2. Check if schools collection exists
    console.log('\n📚 SCHOOLS COLLECTION CHECK');
    console.log('-'.repeat(80));
    try {
      const schoolsRef = collection(db, 'schools');
      const schoolsSnap = await getDocs(schoolsRef);

      if (schoolsSnap.empty) {
        console.log('⚠️  Schools collection is EMPTY');
      } else {
        console.log(`✅ Schools collection exists with ${schoolsSnap.size} school(s):\n`);

        schoolsSnap.forEach((doc) => {
          const data = doc.data();
          console.log(`   • ${doc.id}`);
          console.log(`     Name: ${data.name || 'N/A'}`);
          console.log(`     Slug: ${data.slug || 'N/A'}`);
          console.log(`     Active: ${data.active !== false ? 'Yes' : 'No'}`);
          console.log('');
        });
      }
    } catch (error) {
      console.error('❌ Error reading schools collection:', error.message);
      console.log('   This might be a permissions issue.');
    }

    // 3. Check specific school ID
    const TARGET_SCHOOL_ID = 'hktnikYSv6l6vGPbjMmG';
    console.log(`\n🎯 TARGET SCHOOL CHECK (${TARGET_SCHOOL_ID})`);
    console.log('-'.repeat(80));
    try {
      const schoolRef = doc(db, 'schools', TARGET_SCHOOL_ID);
      const schoolSnap = await getDoc(schoolRef);

      if (schoolSnap.exists()) {
        const data = schoolSnap.data();
        console.log('✅ Target school EXISTS');
        console.log(`   Name: ${data.name || 'N/A'}`);
        console.log(`   Slug: ${data.slug || 'N/A'}`);
        console.log(`   Active: ${data.active !== false ? 'Yes' : 'No'}`);
        console.log(`   Created: ${data.createdAt ? new Date(data.createdAt.seconds * 1000).toLocaleString() : 'N/A'}`);
      } else {
        console.log('❌ Target school does NOT exist');
      }
    } catch (error) {
      console.error('❌ Error reading target school:', error.message);
      console.log('   This might be a permissions issue.');
    }

    // 4. Test schoolService
    console.log('\n🔧 SCHOOL SERVICE TEST');
    console.log('-'.repeat(80));
    try {
      const { default: schoolService } = await import('../services/schoolService');
      const school = await schoolService.getSchool(TARGET_SCHOOL_ID);

      if (school) {
        console.log('✅ schoolService.getSchool() works');
        console.log(`   Returned: ${JSON.stringify(school, null, 2)}`);
      } else {
        console.log('⚠️  schoolService.getSchool() returned null');
      }
    } catch (error) {
      console.error('❌ schoolService.getSchool() failed:', error.message);
    }

    console.log('\n' + '='.repeat(80));
    console.log('CHECK COMPLETE');
    console.log('='.repeat(80) + '\n');

    // Summary
    console.log('📊 SUMMARY & RECOMMENDATIONS:');
    console.log('-'.repeat(80));

    const userRef = doc(db, 'users', currentUser.uid);
    const userSnap = await getDoc(userRef);
    const userData = userSnap.data();

    if (userData.schoolId && !userData.schoolIds) {
      console.log('⚠️  ISSUE: User has schoolId but NOT schoolIds array');
      console.log('   → School selector looks for schoolIds (plural)');
      console.log('   → Need to add schoolIds: [schoolId] to user document');
      console.log(`   → Run: await window.addSchoolIdsArray()`);
    } else if (!userData.schoolId && !userData.schoolIds) {
      console.log('⚠️  ISSUE: User has NEITHER schoolId nor schoolIds');
      console.log('   → User not associated with any school');
    } else {
      console.log('✅ User has schoolIds array correctly set');
    }

  } catch (error) {
    console.error('❌ Check failed:', error);
    throw error;
  }
}

/**
 * Helper: Add schoolIds array based on schoolId
 */
export async function addSchoolIdsArray() {
  try {
    const currentUser = auth.currentUser;

    if (!currentUser) {
      console.error('❌ No user logged in');
      return;
    }

    const { doc: firestoreDoc, getDoc, updateDoc } = await import('firebase/firestore');

    const userRef = firestoreDoc(db, 'users', currentUser.uid);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      console.error('❌ User document not found');
      return;
    }

    const userData = userSnap.data();

    if (!userData.schoolId) {
      console.error('❌ User has no schoolId to copy');
      return;
    }

    if (userData.schoolIds && userData.schoolIds.length > 0) {
      console.log('✅ User already has schoolIds array:', userData.schoolIds);
      return;
    }

    console.log(`Setting schoolIds: [${userData.schoolId}]`);

    await updateDoc(userRef, {
      schoolIds: [userData.schoolId]
    });

    console.log('✅ Updated user with schoolIds array');
    console.log('   Refresh the page to see schools in the selector');

  } catch (error) {
    console.error('❌ Failed to add schoolIds array:', error);
    throw error;
  }
}
